import React from "react";
import { Schedule } from "shared/types/Schedule";
import { IBudgetItem, IArea, IGrupo, ICategoria, IProcedimento, IFace, IElemento } from "shared/types/ClinicalBudget";

interface IProps {
  children: React.ReactNode;
  editMode?: boolean;
  editOrcamentoId?: number;
  initialItems?: IBudgetItem[];
  initialDesconto?: string;
  initialEntrada?: string;
  initialParcelas?: number;
  initialDataVencimento?: string;
  initialObs?: string;
}

export interface IClinicalBudgetContext {
  step: number;
  setStep: (value: number) => void;
  
  editMode: boolean;
  editOrcamentoId: number;
  
  patientObj: Schedule;
  
  budgetItems: IBudgetItem[];
  setBudgetItems: React.Dispatch<React.SetStateAction<IBudgetItem[]>>;
  addItem: (item: Omit<IBudgetItem, 'id'>) => void;
  removeItem: (id: string) => void;
  clearItems: () => void;
  
  areas: IArea[];
  setAreas: (areas: IArea[]) => void;
  loadingAreas: boolean;
  setLoadingAreas: (loading: boolean) => void;
  
  facesRef: IFace[];
  setFacesRef: (faces: IFace[]) => void;
  elementosRef: IElemento[];
  setElementosRef: (elementos: IElemento[]) => void;
  getElementoLabel: (numero: number) => string;
  
  selectedProc: IProcedimento | null;
  setSelectedProc: (proc: IProcedimento | null) => void;
  selectedArea: string;
  setSelectedArea: (area: string) => void;
  
  grupos: IGrupo[];
  setGrupos: (grupos: IGrupo[]) => void;
  loadingGrupos: boolean;
  setLoadingGrupos: (loading: boolean) => void;
  
  selectedGrupo: IGrupo | null;
  setSelectedGrupo: (grupo: IGrupo | null) => void;
  
  // Categorias
  categorias: ICategoria[];
  setCategorias: (cats: ICategoria[]) => void;
  selectedCategoria: ICategoria | null;
  setSelectedCategoria: (cat: ICategoria | null) => void;
  loadingOpcoes: boolean;
  setLoadingOpcoes: (loading: boolean) => void;
  valorPadrao: number;
  setValorPadrao: (valor: number) => void;
  
  // Valor efetivo (categoria ou padrão)
  selectedValor: number;
  setSelectedValor: (valor: number) => void;
  selectedCatprocId: number;
  setSelectedCatprocId: (id: number) => void;
  
  // Odontograma
  selectedTeeth: number[];
  setSelectedTeeth: React.Dispatch<React.SetStateAction<number[]>>;
  showOdontograma: boolean;
  setShowOdontograma: (show: boolean) => void;
  
  // Faces por elemento
  facesPorElemento: Record<string, string>;
  setFacesPorElemento: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  showFacesModal: boolean;
  setShowFacesModal: (show: boolean) => void;
  pendingToothForFaces: number | null;
  setPendingToothForFaces: (tooth: number | null) => void;
  
  // Arcada / Hemi-arcada
  showArcadaModal: boolean;
  setShowArcadaModal: (show: boolean) => void;
  selectedArcadas: number[];
  setSelectedArcadas: React.Dispatch<React.SetStateAction<number[]>>;
  
  // Modal de categorias
  showCategoriasModal: boolean;
  setShowCategoriasModal: (show: boolean) => void;
  
  // Modal de grupos (legado, mantido)
  showGruposModal: boolean;
  setShowGruposModal: (show: boolean) => void;
  
  // Observação personalizada
  showObsModal: boolean;
  setShowObsModal: (show: boolean) => void;
  
  // Configurações do orçamento
  desconto: string;
  setDesconto: (value: string) => void;
  descontoAutorizado: boolean;
  setDescontoAutorizado: (value: boolean) => void;
  obsDesconto: string;
  setObsDesconto: (value: string) => void;
  entrada: string;
  setEntrada: (value: string) => void;
  parcelas: number;
  setParcelas: (value: number) => void;
  dataVencimento: string;
  setDataVencimento: (value: string) => void;
  
  // Cálculos
  subtotal: number;
  total: number;
  totalItens: number;
  fatorDesconto: number;
  
  showValues: boolean;
  setShowValues: (show: boolean) => void;
  
  pacienteValido: boolean;
  setPacienteValido: (valido: boolean) => void;
  mensagemValidacao: string;
  setMensagemValidacao: (msg: string) => void;
  
  saving: boolean;
  setSaving: (saving: boolean) => void;
  
  formatMoeda: (valor: number) => string;
  parseMoeda: (valor: string) => number;
  
  // Validação orto vs outras áreas
  isAreaOrtodontia: (areaNome: string) => boolean;
  temItemOrtodontia: boolean;
  temItemOutraArea: boolean;
  
  // Flags efetivas do procedimento (respeitando override da categoria)
  effectiveUsaArcada: boolean;
  effectiveUsaHemiarcada: boolean;
  effectiveUsaElemento: boolean;
  effectiveQtdFaces: number;
}

const ClinicalBudgetContext = React.createContext({} as IClinicalBudgetContext);

const ClinicalBudgetProvider: React.FC<IProps> = ({
  children,
  editMode = false,
  editOrcamentoId = 0,
  initialItems,
  initialDesconto,
  initialEntrada,
  initialParcelas,
  initialDataVencimento,
  initialObs,
}) => {
  const [step, setStep] = React.useState<number>(1);
  
  const [budgetItems, setBudgetItems] = React.useState<IBudgetItem[]>(initialItems || []);
  
  const [areas, setAreas] = React.useState<IArea[]>([]);
  const [loadingAreas, setLoadingAreas] = React.useState<boolean>(true);
  
  const [facesRef, setFacesRef] = React.useState<IFace[]>([]);
  const [elementosRef, setElementosRef] = React.useState<IElemento[]>([]);
  
  const [selectedProc, setSelectedProc] = React.useState<IProcedimento | null>(null);
  const [selectedArea, setSelectedArea] = React.useState<string>('');
  
  const [grupos, setGrupos] = React.useState<IGrupo[]>([]);
  const [loadingGrupos, setLoadingGrupos] = React.useState<boolean>(false);
  const [selectedGrupo, setSelectedGrupo] = React.useState<IGrupo | null>(null);
  
  // Categorias
  const [categorias, setCategorias] = React.useState<ICategoria[]>([]);
  const [selectedCategoria, setSelectedCategoria] = React.useState<ICategoria | null>(null);
  const [loadingOpcoes, setLoadingOpcoes] = React.useState<boolean>(false);
  const [valorPadrao, setValorPadrao] = React.useState<number>(0);
  const [selectedValor, setSelectedValor] = React.useState<number>(0);
  const [selectedCatprocId, setSelectedCatprocId] = React.useState<number>(0);
  
  // Odontograma
  const [selectedTeeth, setSelectedTeeth] = React.useState<number[]>([]);
  const [showOdontograma, setShowOdontograma] = React.useState<boolean>(false);
  
  // Faces
  const [facesPorElemento, setFacesPorElemento] = React.useState<Record<string, string>>({});
  const [showFacesModal, setShowFacesModal] = React.useState<boolean>(false);
  const [pendingToothForFaces, setPendingToothForFaces] = React.useState<number | null>(null);
  
  // Arcada
  const [showArcadaModal, setShowArcadaModal] = React.useState<boolean>(false);
  const [selectedArcadas, setSelectedArcadas] = React.useState<number[]>([]);
  
  // Modais
  const [showCategoriasModal, setShowCategoriasModal] = React.useState<boolean>(false);
  const [showGruposModal, setShowGruposModal] = React.useState<boolean>(false);
  const [showObsModal, setShowObsModal] = React.useState<boolean>(false);
  
  // Configurações
  const [desconto, setDesconto] = React.useState<string>(initialDesconto || 'R$ 0,00');
  const [descontoAutorizado, setDescontoAutorizado] = React.useState<boolean>(
    initialDesconto ? parseFloat(initialDesconto.replace('R$', '').replace(/\s/g, '').replace(/\./g, '').replace(',', '.') || '0') > 0 : false
  );
  const [obsDesconto, setObsDesconto] = React.useState<string>(initialObs || '');
  const [entrada, setEntrada] = React.useState<string>(initialEntrada || 'R$ 0,00');
  const [parcelas, setParcelas] = React.useState<number>(initialParcelas || 1);
  const [dataVencimento, setDataVencimento] = React.useState<string>(initialDataVencimento || '');
  
  const [showValues, setShowValues] = React.useState<boolean>(false);
  
  const [pacienteValido, setPacienteValido] = React.useState<boolean>(true);
  const [mensagemValidacao, setMensagemValidacao] = React.useState<string>('');
  
  const [saving, setSaving] = React.useState<boolean>(false);

  const patientActually = localStorage.getItem("personAttendance");
  const patientObj = patientActually ? JSON.parse(patientActually) : null;
  
  React.useEffect(() => {
    if (patientObj && patientObj.dentista_faz_orc !== undefined) {
      setShowValues(patientObj.dentista_faz_orc === 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!dataVencimento) {
      const hoje = new Date();
      hoje.setDate(hoje.getDate() + 30);
      const dia = String(hoje.getDate()).padStart(2, '0');
      const mes = String(hoje.getMonth() + 1).padStart(2, '0');
      const ano = hoje.getFullYear();
      setDataVencimento(`${dia}/${mes}/${ano}`);
    }
  }, [dataVencimento]);

  const formatMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const parseMoeda = (valor: string): number => {
    const cleaned = valor.replace('R$', '').replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const subtotal = React.useMemo(() => {
    return budgetItems.reduce((acc, item) => acc + (item.valor * item.qtde), 0);
  }, [budgetItems]);

  const descontoParsed = React.useMemo(() => {
    return parseMoeda(desconto);
  }, [desconto]);

  const fatorDesconto = React.useMemo(() => {
    return subtotal > 0 ? descontoParsed / subtotal : 0;
  }, [subtotal, descontoParsed]);

  const total = React.useMemo(() => {
    const result = subtotal - descontoParsed;
    return result < 0 ? 0 : result;
  }, [subtotal, descontoParsed]);

  const totalItens = React.useMemo(() => {
    return budgetItems.reduce((acc, item) => acc + item.qtde, 0);
  }, [budgetItems]);

  const isAreaOrtodontia = React.useCallback((areaNome: string): boolean => {
    return areaNome.toLowerCase().includes('orto');
  }, []);

  const temItemOrtodontia = React.useMemo(() => {
    return budgetItems.some(item => isAreaOrtodontia(item.area));
  }, [budgetItems, isAreaOrtodontia]);

  const temItemOutraArea = React.useMemo(() => {
    return budgetItems.some(item => !isAreaOrtodontia(item.area));
  }, [budgetItems, isAreaOrtodontia]);

  // Flags efetivas: se há categoria selecionada, usar override, senão usar do proc
  const effectiveUsaArcada = React.useMemo(() => {
    if (selectedCategoria) return selectedCategoria.usa_arcada;
    return selectedProc?.usa_arcada ?? false;
  }, [selectedCategoria, selectedProc]);

  const effectiveUsaHemiarcada = React.useMemo(() => {
    if (selectedCategoria) return selectedCategoria.usa_hemiarcada;
    return selectedProc?.usa_hemiarcada ?? false;
  }, [selectedCategoria, selectedProc]);

  const effectiveUsaElemento = React.useMemo(() => {
    return selectedProc?.usa_elemento ?? false;
  }, [selectedProc]);

  const effectiveQtdFaces = React.useMemo(() => {
    return selectedProc?.qtd_faces ?? 0;
  }, [selectedProc]);

  const getElementoLabel = React.useCallback((numero: number): string => {
    if (numero === 0) return 'N/A';
    const el = elementosRef.find(e => e.numero === numero);
    return el ? el.descricao : String(numero);
  }, [elementosRef]);

  const addItem = (item: Omit<IBudgetItem, 'id'>) => {
    const newItem: IBudgetItem = {
      ...item,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    setBudgetItems(prev => [...prev, newItem]);
  };

  const removeItem = (id: string) => {
    setBudgetItems(prev => prev.filter(item => item.id !== id));
  };

  const clearItems = () => {
    setBudgetItems([]);
  };

  const value = React.useMemo(
    () => ({
      step, setStep,
      editMode,
      editOrcamentoId,
      patientObj,
      budgetItems, setBudgetItems, addItem, removeItem, clearItems,
      areas, setAreas, loadingAreas, setLoadingAreas,
      facesRef, setFacesRef, elementosRef, setElementosRef, getElementoLabel,
      selectedProc, setSelectedProc, selectedArea, setSelectedArea,
      grupos, setGrupos, loadingGrupos, setLoadingGrupos,
      selectedGrupo, setSelectedGrupo,
      categorias, setCategorias, selectedCategoria, setSelectedCategoria,
      loadingOpcoes, setLoadingOpcoes, valorPadrao, setValorPadrao,
      selectedValor, setSelectedValor, selectedCatprocId, setSelectedCatprocId,
      selectedTeeth, setSelectedTeeth, showOdontograma, setShowOdontograma,
      facesPorElemento, setFacesPorElemento, showFacesModal, setShowFacesModal,
      pendingToothForFaces, setPendingToothForFaces,
      showArcadaModal, setShowArcadaModal, selectedArcadas, setSelectedArcadas,
      showCategoriasModal, setShowCategoriasModal,
      showGruposModal, setShowGruposModal,
      showObsModal, setShowObsModal,
      desconto, setDesconto, descontoAutorizado, setDescontoAutorizado,
      obsDesconto, setObsDesconto, entrada, setEntrada,
      parcelas, setParcelas, dataVencimento, setDataVencimento,
      subtotal, total, totalItens, fatorDesconto,
      showValues, setShowValues,
      pacienteValido, setPacienteValido, mensagemValidacao, setMensagemValidacao,
      saving, setSaving,
      formatMoeda, parseMoeda,
      isAreaOrtodontia, temItemOrtodontia, temItemOutraArea,
      effectiveUsaArcada, effectiveUsaHemiarcada, effectiveUsaElemento, effectiveQtdFaces,
    }),
    [
      step, editMode, editOrcamentoId, patientObj, budgetItems, areas, loadingAreas,
      selectedProc, selectedArea, grupos, loadingGrupos, selectedGrupo,
      categorias, selectedCategoria, loadingOpcoes, valorPadrao,
      selectedValor, selectedCatprocId,
      selectedTeeth, showOdontograma,
      facesPorElemento, showFacesModal, pendingToothForFaces,
      showArcadaModal, selectedArcadas,
      showCategoriasModal, showGruposModal, showObsModal,
      desconto, descontoAutorizado, obsDesconto, entrada,
      parcelas, dataVencimento,
      subtotal, total, totalItens, fatorDesconto,
      showValues, pacienteValido, mensagemValidacao, saving,
      isAreaOrtodontia, temItemOrtodontia, temItemOutraArea,
      effectiveUsaArcada, effectiveUsaHemiarcada, effectiveUsaElemento, effectiveQtdFaces,
      elementosRef, facesRef, getElementoLabel,
    ]
  );

  return (
    <ClinicalBudgetContext.Provider value={value}>
      {children}
    </ClinicalBudgetContext.Provider>
  );
};

const useClinicalBudget = (): IClinicalBudgetContext =>
  React.useContext(ClinicalBudgetContext);

export { ClinicalBudgetContext, ClinicalBudgetProvider, useClinicalBudget };
